import { z } from 'zod';
import { isRelativeDateRangeToken, resolveRelativeDateRange } from '../date-ranges.js';
import { CONDITION_OPERATORS, type ConditionOperator } from '../enums.js';

/**
 * Workflow condition grammar
 * ==========================
 * A rule builder emits a tree of AND/OR groups whose leaves compare one field
 * against one value. Evaluation is a pure function of the tree and the record,
 * with no database access and no clock — which is what makes an automation
 * engine testable rather than something you debug in production.
 *
 * The same grammar backs saved list views and assignment rules, so a filter a
 * user builds in one place reads the same everywhere.
 */

export const MAX_CONDITION_DEPTH = 5;

export interface ConditionRule {
  field: string;
  operator: ConditionOperator;
  value?: unknown;
}

export interface ConditionGroup {
  op: 'AND' | 'OR';
  rules: ConditionNode[];
}

export type ConditionNode = ConditionRule | ConditionGroup;

export function isGroup(node: ConditionNode): node is ConditionGroup {
  return 'op' in node && 'rules' in node;
}

/** Recursive schema: a group nests groups, bounded by MAX_CONDITION_DEPTH. */
export const conditionRuleSchema = z.object({
  field: z.string().min(1).max(120),
  operator: z.enum(CONDITION_OPERATORS),
  value: z.unknown().optional(),
});

export const conditionNodeSchema: z.ZodType<ConditionNode> = z.lazy(() =>
  z.union([
    conditionRuleSchema,
    z.object({
      op: z.enum(['AND', 'OR']),
      rules: z.array(conditionNodeSchema).max(20),
    }),
  ]),
);

/**
 * An empty object is a valid, always-true condition — a workflow with no
 * conditions fires on every matching event, which is the common case.
 */
export const conditionTreeSchema = z.union([z.object({}).loose(), conditionNodeSchema]);

export interface EvaluationContext {
  /** The record as it stands after the change. */
  record: Record<string, unknown>;
  /** The record before the change; required for the CHANGED operator. */
  previous?: Record<string, unknown> | null;
  /** Fields that actually changed, so CHANGED does not re-derive them. */
  changedFields?: readonly string[];
}

/**
 * Reads a possibly-dotted path, so a rule can address a custom field
 * (`customFields.territory`) as naturally as a column (`amount`).
 */
export function resolveField(source: Record<string, unknown>, path: string): unknown {
  if (!path.includes('.')) return source[path];

  let cursor: unknown = source;
  for (const segment of path.split('.')) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Coerces to a number for ordered comparisons. Money arrives as a decimal
 * string and dates as ISO strings, so both have to be understood here or every
 * "amount greater than" rule silently fails.
 */
function toComparableNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber)) return asNumber;
    const asDate = Date.parse(trimmed);
    return Number.isNaN(asDate) ? null : asDate;
  }
  return null;
}

/** Normalises for equality: dates and decimals compare by value, not identity. */
function toComparableString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function equals(left: unknown, right: unknown): boolean {
  if (isBlank(left) && isBlank(right)) return true;

  // Numbers first, so "100" and 100 and "100.00" all agree.
  const leftNumber = toComparableNumber(left);
  const rightNumber = toComparableNumber(right);
  if (leftNumber !== null && rightNumber !== null) return leftNumber === rightNumber;

  if (typeof left === 'boolean' || typeof right === 'boolean') {
    return Boolean(left) === Boolean(right);
  }

  return toComparableString(left).toLowerCase() === toComparableString(right).toLowerCase();
}

function contains(haystack: unknown, needle: unknown): boolean {
  if (Array.isArray(haystack)) return haystack.some((entry) => equals(entry, needle));
  return toComparableString(haystack)
    .toLowerCase()
    .includes(toComparableString(needle).toLowerCase());
}

function membership(value: unknown, options: unknown): boolean {
  const list = Array.isArray(options) ? options : [options];
  return list.some((option) => equals(value, option));
}

function compareOrdered(left: unknown, right: unknown): number | null {
  const leftNumber = toComparableNumber(left);
  const rightNumber = toComparableNumber(right);
  if (leftNumber === null || rightNumber === null) return null;
  return leftNumber - rightNumber;
}

/** True when `actual` falls within the inclusive `[from, to]` calendar-date range. */
function withinRange(actual: unknown, from: string, to: string): boolean {
  const actualTime = toComparableNumber(actual);
  const fromTime = Date.parse(`${from}T00:00:00.000Z`);
  // Exclusive end at the start of the day *after* `to`, so the whole final
  // day counts — see the identical reasoning in `report-query.ts`.
  const toTime = Date.parse(`${to}T00:00:00.000Z`) + 24 * 60 * 60 * 1000;
  if (actualTime === null || Number.isNaN(fromTime) || Number.isNaN(toTime)) return false;
  return actualTime >= fromTime && actualTime < toTime;
}

/** Evaluates one leaf. Anything it cannot compare is false, never a throw. */
export function evaluateRule(rule: ConditionRule, context: EvaluationContext): boolean {
  const actual = resolveField(context.record, rule.field);

  switch (rule.operator) {
    case 'EQUALS':
      return equals(actual, rule.value);
    case 'NOT_EQUALS':
      return !equals(actual, rule.value);
    case 'CONTAINS':
      return contains(actual, rule.value);
    case 'NOT_CONTAINS':
      return !contains(actual, rule.value);
    case 'STARTS_WITH':
      return toComparableString(actual)
        .toLowerCase()
        .startsWith(toComparableString(rule.value).toLowerCase());
    case 'ENDS_WITH':
      return toComparableString(actual)
        .toLowerCase()
        .endsWith(toComparableString(rule.value).toLowerCase());
    case 'GREATER_THAN': {
      const diff = compareOrdered(actual, rule.value);
      return diff !== null && diff > 0;
    }
    case 'GREATER_OR_EQUAL': {
      const diff = compareOrdered(actual, rule.value);
      return diff !== null && diff >= 0;
    }
    case 'LESS_THAN': {
      const diff = compareOrdered(actual, rule.value);
      return diff !== null && diff < 0;
    }
    case 'LESS_OR_EQUAL': {
      const diff = compareOrdered(actual, rule.value);
      return diff !== null && diff <= 0;
    }
    case 'IS_EMPTY':
      return isBlank(actual);
    case 'IS_NOT_EMPTY':
      return !isBlank(actual);
    case 'IN':
      return membership(actual, rule.value);
    case 'NOT_IN':
      return !membership(actual, rule.value);
    case 'BETWEEN': {
      const [from, to] = Array.isArray(rule.value) ? rule.value : [];
      return typeof from === 'string' && typeof to === 'string' && withinRange(actual, from, to);
    }
    case 'RELATIVE_DATE_RANGE': {
      if (!isRelativeDateRangeToken(rule.value)) return false;
      const { from, to } = resolveRelativeDateRange(rule.value);
      return withinRange(actual, from, to);
    }
    case 'CHANGED': {
      // Prefer the caller's diff; fall back to comparing against `previous` so
      // the operator still works when only the old record is available.
      if (context.changedFields) return context.changedFields.includes(rule.field);
      if (!context.previous) return false;
      return !equals(actual, resolveField(context.previous, rule.field));
    }
  }
}

/**
 * Evaluates a whole tree.
 *
 * An empty tree matches — a workflow with no conditions is meant to fire on
 * every event of its trigger type. Depth is bounded so a hand-crafted payload
 * cannot make evaluation recurse without limit.
 */
export function evaluateConditions(tree: unknown, context: EvaluationContext, depth = 0): boolean {
  if (!tree || typeof tree !== 'object') return true;
  if (depth > MAX_CONDITION_DEPTH) return false;

  const node = tree as Partial<ConditionGroup> & Partial<ConditionRule>;

  if (Array.isArray(node.rules)) {
    // An empty group has nothing to disagree with, so it matches.
    if (node.rules.length === 0) return true;
    const results = node.rules.map((child) => evaluateConditions(child, context, depth + 1));
    return node.op === 'OR' ? results.some(Boolean) : results.every(Boolean);
  }

  if (typeof node.field === 'string' && typeof node.operator === 'string') {
    return evaluateRule(node as ConditionRule, context);
  }

  // Neither a group nor a rule: an empty `{}`, which matches everything.
  return true;
}

/** Human-readable summary of a tree, for the workflow list and audit entries. */
export function describeConditions(tree: unknown, depth = 0): string {
  if (!tree || typeof tree !== 'object' || depth > MAX_CONDITION_DEPTH) return 'always';

  const node = tree as Partial<ConditionGroup> & Partial<ConditionRule>;

  if (Array.isArray(node.rules)) {
    if (node.rules.length === 0) return 'always';
    const parts = node.rules.map((child) => describeConditions(child, depth + 1));
    const joined = parts.join(node.op === 'OR' ? ' or ' : ' and ');
    return depth === 0 ? joined : `(${joined})`;
  }

  if (typeof node.field === 'string' && typeof node.operator === 'string') {
    const operator = node.operator.toLowerCase().replace(/_/gu, ' ');
    const value = node.value;
    if (
      node.operator === 'IS_EMPTY' ||
      node.operator === 'IS_NOT_EMPTY' ||
      node.operator === 'CHANGED'
    ) {
      return `${node.field} ${operator}`;
    }
    return `${node.field} ${operator} ${Array.isArray(value) ? value.join(', ') : String(value)}`;
  }

  return 'always';
}
